/**
 * Acting on an alert is a durable intent about the underlying object, not a
 * change to what this browser happens to be showing.
 *
 * Dismissing an alert in one tab while offline must survive a reload, must not
 * pretend the server agreed, and must still be recognisable as *this alert about
 * that object* when it is finally sent. So an action is recorded here as a
 * queued intent carrying its subject, and stays visibly pending until the
 * server accepts it.
 */

import { formatDestination, type Destination } from "./destinations.js";
import type { KeyValueStore } from "./key-value-store.js";

export const ALERT_INTENTS_KEY = "recallvault.alertIntents";

/** Mirrors the server's `ALERT_ACTIONS` in `alert-types.ts`. */
export type AlertAction = "snooze" | "dismiss" | "resolve";

export interface AlertIntent {
  readonly id: string;
  readonly alertId: string;
  readonly ownerAccountId?: string;
  readonly action: AlertAction;
  /** The object the alert is about, so the intent is not merely about a row. */
  readonly subject: string;
  readonly at: string;
  readonly snoozeSeconds?: number;
  /** Set once the server has accepted this intent. */
  readonly sentAt?: string;
  /** Set when sending failed, so the row can say why rather than disappear. */
  readonly failure?: string;
}

function isIntent(value: unknown): value is AlertIntent {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === "string"
    && typeof record.alertId === "string"
    && (record.action === "snooze" || record.action === "dismiss" || record.action === "resolve")
    && typeof record.subject === "string"
    && typeof record.at === "string";
}

function intentsKey(ownerAccountId?: string): string {
  return ownerAccountId ? `${ALERT_INTENTS_KEY}:${ownerAccountId}` : ALERT_INTENTS_KEY;
}

export function readIntents(store: KeyValueStore | undefined, ownerAccountId?: string): readonly AlertIntent[] {
  try {
    const raw = store?.getItem(intentsKey(ownerAccountId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isIntent).filter((intent) => ownerAccountId === undefined || intent.ownerAccountId === ownerAccountId) : [];
  } catch {
    return [];
  }
}

function writeIntents(store: KeyValueStore | undefined, intents: readonly AlertIntent[], ownerAccountId = intents[0]?.ownerAccountId): readonly AlertIntent[] {
  try {
    store?.setItem(intentsKey(ownerAccountId), JSON.stringify(intents));
  } catch {
    /* Private mode or a full quota: the intent is still correct in memory. */
  }
  return intents;
}

/**
 * Records a decision about an alert. The intent names the destination of the
 * object the alert explains, so a later reader can tell what was acted on even
 * if the alert itself has since been purged by retention.
 */
export function enqueueIntent(
  store: KeyValueStore | undefined,
  current: readonly AlertIntent[],
  input: {
    readonly alertId: string;
    readonly ownerAccountId?: string;
    readonly action: AlertAction;
    readonly subject: Destination;
    readonly snoozeSeconds?: number;
    readonly at?: string;
    readonly id?: string;
  },
): readonly AlertIntent[] {
  const at = input.at ?? new Date().toISOString();
  const intent: AlertIntent = {
    id: input.id ?? `intent:${input.alertId}:${at}`,
    alertId: input.alertId,
    ...(input.ownerAccountId === undefined ? {} : { ownerAccountId: input.ownerAccountId }),
    action: input.action,
    subject: formatDestination(input.subject),
    at,
    ...(input.snoozeSeconds === undefined ? {} : { snoozeSeconds: input.snoozeSeconds }),
  };
  // One live decision per alert: a later action supersedes an unsent earlier one.
  const kept = current.filter((existing) => existing.alertId !== intent.alertId || existing.sentAt !== undefined);
  return writeIntents(store, [intent, ...kept], input.ownerAccountId);
}

/** Intents the server has not accepted yet. These are what a sync must send. */
export function pendingIntents(intents: readonly AlertIntent[]): readonly AlertIntent[] {
  return intents.filter((intent) => intent.sentAt === undefined);
}

/**
 * Both mark functions take the caller's current list rather than re-reading.
 *
 * A store that throws — private browsing, or site data blocked — makes
 * `readIntents` return `[]`, so re-reading here would map over nothing and hand
 * back an empty list. The caller assigns that straight back, and the decision
 * the user just made disappears from memory as well as from disk. Storage
 * failing must cost persistence, never the decision itself.
 */
export function markIntentSent(
  store: KeyValueStore | undefined,
  current: readonly AlertIntent[],
  intentId: string,
  sentAt: string = new Date().toISOString(),
): readonly AlertIntent[] {
  const next = current.map((intent) => (intent.id === intentId ? { ...intent, sentAt, failure: undefined } : intent));
  writeIntents(store, next);
  return next;
}

export function markIntentFailed(
  store: KeyValueStore | undefined,
  current: readonly AlertIntent[],
  intentId: string,
  failure: string,
): readonly AlertIntent[] {
  const next = current.map((intent) => (intent.id === intentId ? { ...intent, failure } : intent));
  writeIntents(store, next);
  return next;
}

/** Drops accepted intents once the server's own state is the record. */
export function forgetSentIntents(store: KeyValueStore | undefined): readonly AlertIntent[] {
  return writeIntents(store, pendingIntents(readIntents(store)));
}

/** The queued decision for one alert, if the user has made one. */
export function intentForAlert(intents: readonly AlertIntent[], alertId: string): AlertIntent | undefined {
  return pendingIntents(intents).find((intent) => intent.alertId === alertId);
}
